import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Button } from './Button';

type Props = {
  children: ReactNode;
  /** Optional custom fallback. Receives the error + reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Optional error reporter (e.g. Sentry.captureException). */
  onError?: (error: Error, info: ErrorInfo) => void;
};

type State = {
  error: Error | null;
};

/**
 * Catches render-time errors in its subtree and shows a fallback UI.
 * Wrap the whole app for a global safety net, or individual routes
 * for finer isolation.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-red-200 bg-red-50 p-6 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-red-800">Something went wrong</h2>
          <p className="mt-2 text-sm text-red-700">
            {error.message || 'An unexpected error occurred while rendering this page.'}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button variant="destructive" onClick={this.reset}>
              Try again
            </Button>
            <Button variant="ghost" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
